/**
 * Admin — user management. List, invite, enable/disable, delete.
 *
 * Every action talks to the HTTP admin API; errors come back as plain
 * strings and are rendered inline. The page re-fetches the user list
 * after each mutation so state never drifts from the server.
 *
 * Delete is gated behind a confirm() dialog because it's irreversible.
 */
import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  AdminUser,
  deleteUser,
  disableUser,
  enableUser,
  inviteUser,
  listUsers,
} from '../lib/adminApi';

export default function Admin() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);

  // Per-row busy state so buttons don't all spin when one row acts.
  const [busySub, setBusySub] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const resp = await listUsers();
      setUsers(resp.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInviteMessage(null);
    if (!inviteEmail) return;
    setInviting(true);
    try {
      await inviteUser(inviteEmail);
      setInviteMessage(`Invite sent to ${inviteEmail}.`);
      setInviteEmail('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed');
    } finally {
      setInviting(false);
    }
  }

  async function onToggle(user: AdminUser) {
    setError(null);
    setBusySub(user.sub);
    try {
      if (user.enabled) {
        await disableUser(user.sub);
      } else {
        await enableUser(user.sub);
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toggle failed');
    } finally {
      setBusySub(null);
    }
  }

  async function onDelete(user: AdminUser) {
    const confirmed = window.confirm(
      `Delete ${user.email ?? user.sub}? This cannot be undone.`,
    );
    if (!confirmed) return;
    setError(null);
    setBusySub(user.sub);
    try {
      await deleteUser(user.sub);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusySub(null);
    }
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 'var(--sp-6)' }}>
      <h1>User admin</h1>

      <section
        style={{
          background: 'var(--bg-raised)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 'var(--sp-6)',
          marginBottom: 'var(--sp-6)',
        }}
      >
        <h2 style={{ marginTop: 0 }}>Invite a player</h2>
        <p style={{ color: 'var(--fg-muted)', marginTop: 0 }}>
          Sends a Cognito invitation email with a temporary password. The
          user sets their permanent password on first sign-in.
        </p>
        <form
          onSubmit={onInvite}
          style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end' }}
        >
          <label style={{ display: 'grid', gap: 'var(--sp-1)', flex: 1 }}>
            <span>Email</span>
            <input
              type="email"
              inputMode="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
          <button type="submit" disabled={inviting} style={primaryButton(inviting)}>
            {inviting ? 'Sending…' : 'Invite'}
          </button>
        </form>
        {inviteMessage && (
          <p style={{ color: 'var(--active)', marginBottom: 0 }}>{inviteMessage}</p>
        )}
      </section>

      <h2>Users</h2>
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading ? (
        <p>Loading…</p>
      ) : users.length === 0 ? (
        <p style={{ color: 'var(--fg-muted)' }}>No users yet.</p>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            background: 'var(--bg-raised)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <thead>
            <tr style={{ textAlign: 'left', background: 'var(--bg-elevated)' }}>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Enabled</th>
              <th style={thStyle}>Created</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const busy = busySub === u.sub;
              return (
                <tr key={u.sub} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={tdStyle}>{u.email ?? <em>(no email)</em>}</td>
                  <td style={tdStyle}>
                    <span style={{ color: statusColor(u.status) }}>{u.status}</span>
                  </td>
                  <td style={tdStyle}>{u.enabled ? 'Yes' : 'No'}</td>
                  <td style={tdStyle}>
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => onToggle(u)}
                      disabled={busy}
                      style={secondaryButton(busy)}
                    >
                      {u.enabled ? 'Disable' : 'Enable'}
                    </button>{' '}
                    <button
                      type="button"
                      onClick={() => onDelete(u)}
                      disabled={busy}
                      style={dangerButton(busy)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function statusColor(status: string): string {
  switch (status) {
    case 'CONFIRMED':
      return 'var(--active)';
    case 'FORCE_CHANGE_PASSWORD':
      return 'var(--warning)';
    case 'RESET_REQUIRED':
      return 'var(--warning)';
    default:
      return 'var(--fg-base)';
  }
}

const inputStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--bg-base)',
  color: 'var(--fg-strong)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontFamily: 'var(--font-body)',
  fontSize: 16,
};

const thStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  color: 'var(--fg-strong)',
  letterSpacing: '0.02em',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  color: 'var(--fg-base)',
  verticalAlign: 'middle',
};

function primaryButton(busy: boolean): React.CSSProperties {
  return {
    padding: 'var(--sp-2) var(--sp-4)',
    background: 'var(--sheet-a)',
    color: 'var(--bg-deep)',
    border: 'none',
    borderRadius: 4,
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: busy ? 'wait' : 'pointer',
  };
}

function secondaryButton(busy: boolean): React.CSSProperties {
  return {
    padding: 'var(--sp-1) var(--sp-3)',
    background: 'transparent',
    color: 'var(--fg-base)',
    border: '1px solid var(--border-strong)',
    borderRadius: 4,
    cursor: busy ? 'wait' : 'pointer',
  };
}

function dangerButton(busy: boolean): React.CSSProperties {
  return {
    padding: 'var(--sp-1) var(--sp-3)',
    background: 'transparent',
    color: 'var(--danger)',
    border: '1px solid var(--danger)',
    borderRadius: 4,
    cursor: busy ? 'wait' : 'pointer',
  };
}
